import { useState, DragEvent, TouchEvent } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import styles from "./BlockGridInput.module.css";
import GridBlock from "./GridBlock";
import AddIcon from "@mui/icons-material/Add";

export interface Block {
  id: string;
  label: string;
  icon?: string;
}

interface BlockGridInputProps {
  value: string[];
  onChange: (newOrder: string[]) => void;
  availableBlocks: Block[];
  gridColumns?: number;
  label?: string;
  disabled?: boolean;
  onAddBlock?: () => void;
  gridClassName?: string; 
  blockClassName?: string; 
  blockIconClassName?: string; 
  blockLabelClassName?: string; 
}

export default function BlockGridInput({
  value = [],
  onChange,
  availableBlocks,
  gridColumns = 3,
  label,
  disabled = false,
  onAddBlock,
  gridClassName,
  blockClassName,
  blockIconClassName,
  blockLabelClassName,
}: BlockGridInputProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [currentOrder, setCurrentOrder] = useState<string[]>(value);
  const [touchStartY, setTouchStartY] = useState<number>(0);
  const [touchStartX, setTouchStartX] = useState<number>(0);

  const handleDragStart = (e: DragEvent<HTMLDivElement>, index: number) => {
    if (disabled) return;
    setDraggedIndex(index);
    setCurrentOrder(value);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    if (currentOrder !== value) {
      onChange(currentOrder);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (index: number) => {
    if (disabled || draggedIndex === null || draggedIndex === index) return;

    const newOrder = Array.from(currentOrder);
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedItem);

    setCurrentOrder(newOrder);
    setDraggedIndex(index);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Touch for mobile
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>, index: number) => {
    if (disabled) return;
    const touch = e.touches[0];
    setTouchStartX(touch.clientX);
    setTouchStartY(touch.clientY);
    setDraggedIndex(index);
    setCurrentOrder(value);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (disabled || draggedIndex === null) return;
    e.preventDefault();

    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    
    if (element) {
      const blockElement = element.closest('[data-block-index]');
      if (blockElement) {
        const targetIndex = parseInt(blockElement.getAttribute('data-block-index') || '0');
        if (targetIndex !== draggedIndex) {
          handleDragEnter(targetIndex);
        }
      }
    }
  };

  const handleTouchEnd = () => {
    if (draggedIndex !== null) {
      if (currentOrder !== value) {
        onChange(currentOrder);
      }
      setDraggedIndex(null);
    }
  };

  const getBlockData = (blockId: string) => {
    return availableBlocks.find((b) => b.id === blockId);
  };

  const displayOrder = draggedIndex !== null ? currentOrder : value;

  return (
    <Box className={styles.container}>
      {label && (
        <Box className={styles.header}>
          <Typography variant="h6">{label}</Typography>
          {onAddBlock && (
            <IconButton 
              onClick={onAddBlock} 
              disabled={disabled}
              size="small"
              color="primary"
            >
              <AddIcon />
            </IconButton>
          )}
        </Box>
      )}

      <div className={gridClassName || styles.defaultGrid}>
        {displayOrder.length === 0 ? (
          <Typography color="text.secondary" align="center" className={styles.empty}>
            No blocks added
          </Typography>
        ) : (
          <>
            {displayOrder.map((blockId, index) => {
              const blockData = getBlockData(blockId);
              if (!blockData) return null;

              const isDragging = draggedIndex === index;

              return (
                <GridBlock
                  key={blockId}
                  block={blockData}
                  index={index}
                  isDragging={isDragging}
                  disabled={disabled}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDragEnter={() => handleDragEnter(index)}
                  onDrop={handleDrop}
                  onTouchStart={(e) => handleTouchStart(e, index)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  blockClassName={blockClassName}
                  blockIconClassName={blockIconClassName}
                  blockLabelClassName={blockLabelClassName}
                />
              );
            })}
          </>
        )}
      </div>
    </Box>
  );
}